import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import cx from 'classnames';
import { useMemo } from 'react';
import { DropzoneInputProps, FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

import { t } from '@core/i18n';
import { Button, ButtonProps } from '@shared/components/button-new';
import { showNotification } from '@shared/components/notification';
import { mimeGroups, MimeToExtensions } from '@shared/constants/common';
import mimeDb from '@shared/constants/mimeDb';

import { styles } from './FileDropzone.styles';

// ****************************************************
// * !! DO NOT USE COMPONENT DIRECTLY              !! *
// * !! USE SingleFileDropzone / MultiFileDropzone !! *
// ****************************************************

export enum FileType {
  brochure = 'brochure',
  epc = 'epc',
  files = 'files',
  floorPlans = 'floorPlans',
  goadPlans = 'goadPlans',
  images = 'images',
  noArchiveFiles = 'noArchiveFiles',
}

export const getAccept = (suppliedTypes: (keyof typeof mimeGroups)[]) => {
  const accept: MimeToExtensions = {};

  suppliedTypes.forEach((key) => {
    mimeGroups[key].forEach((type) => {
      accept[type] = mimeDb[type].extensions.map((ext) => `.${ext}`);
    });
  });

  return accept;
};

export const getAcceptString = (suppliedTypes: (keyof typeof mimeGroups)[]) => {
  const types: string[] = [];

  suppliedTypes.forEach((key) => {
    mimeGroups[key].forEach((type) => {
      if (!types.includes(type)) {
        types.push(type);
      }

      mimeDb[type].extensions.forEach((ext) => {
        if (!types.includes(ext)) {
          types.push(ext);
        }
      });
    });
  });

  return types.join(',');
};

export interface UploadingFileInfo {
  accept: MimeToExtensions;
  acceptString: string;
  acceptTooltip: string;
}

export const getUploadingFileInfo = (type: FileType): UploadingFileInfo => {
  switch (type) {
    case FileType.brochure:
      return {
        accept: getAccept(['pdf', 'document', 'presentation']),
        acceptString: getAcceptString(['pdf', 'document', 'presentation']),
        acceptTooltip: '.pdf, word, powerpoint',
      };

    case FileType.epc:
      return {
        accept: getAccept(['pdf', 'document', 'presentation', 'images']),
        acceptString: getAcceptString(['pdf', 'document', 'presentation', 'images']),
        acceptTooltip: '.pdf, word, powerpoint, .jpg, .png',
      };

    case FileType.files:
      return {
        accept: getAccept(['pdf', 'document', 'presentation', 'spreadsheet', 'images', 'cad', 'zip']),
        acceptString: getAcceptString(['pdf', 'document', 'presentation', 'spreadsheet', 'images', 'cad', 'zip']),
        acceptTooltip: '.pdf, word, powerpoint, excel, .jpg, .png, .dwg, .zip',
      };
    case FileType.noArchiveFiles:
      return {
        accept: getAccept(['pdf', 'document', 'presentation', 'spreadsheet', 'images', 'cad']),
        acceptString: getAcceptString(['pdf', 'document', 'presentation', 'spreadsheet', 'images', 'cad']),
        acceptTooltip: '.pdf, word, powerpoint, excel, .jpg, .png, .dwg',
      };

    case FileType.floorPlans:
      return {
        accept: getAccept(['pdf', 'images', 'cad']),
        acceptString: getAcceptString(['pdf', 'images', 'cad']),
        acceptTooltip: '.pdf, .jpg, .png, .dwg',
      };

    case FileType.goadPlans:
      return {
        accept: getAccept(['pdf', 'images']),
        acceptString: getAcceptString(['pdf', 'images']),
        acceptTooltip: '.pdf, .jpg, .png',
      };

    case FileType.images:
      return {
        accept: getAccept(['images']),
        acceptString: getAcceptString(['images']),
        acceptTooltip: '.jpg, .png',
      };
  }
};

// NOTE: This is a workaround to support files which cannot obtain a mime type within the browser
export const extensionsWithBlankTypeInBrowser = ['dwg'];

export const isValidFileType = (file: File, validExtensions: string[]): boolean => {
  if (file.type === '') {
    const fileExtension = file.name.split('.').pop()?.toLowerCase() ?? '';

    return extensionsWithBlankTypeInBrowser.includes(fileExtension);
  }

  return validExtensions.includes(file.type);
};

// ****************************************************
// * !! DO NOT USE COMPONENT DIRECTLY              !! *
// * !! USE SingleFileDropzone / MultiFileDropzone !! *
// ****************************************************

export interface FileDropzoneProps extends WithStyles<typeof styles> {
  buttonProps?: Omit<ButtonProps, 'classes'>;
  inputProps?: DropzoneInputProps;
  maxSize?: number;
  multiple: boolean;
  noClick?: boolean;
  type: FileType;
  withTooltip?: boolean;
  onReject?: (fileRejections: FileRejection[]) => void;
  onUpload: (files: File[]) => void;
}

// ****************************************************
// * !! DO NOT USE COMPONENT DIRECTLY              !! *
// * !! USE SingleFileDropzone / MultiFileDropzone !! *
// ****************************************************

const FileDropzoneComponent: React.FC<FileDropzoneProps> = ({
  buttonProps,
  children,
  classes,
  inputProps,
  maxSize,
  multiple,
  noClick,
  type,
  withTooltip = true,
  onReject,
  onUpload,
}) => {
  const uploadingFileInfo = useMemo(() => getUploadingFileInfo(type), [type]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: uploadingFileInfo.accept,
    maxSize,
    multiple,
    noClick,
    onDropAccepted: (acceptedFiles) => {
      if (acceptedFiles.length) {
        onUpload(acceptedFiles);
      }
    },
    onDropRejected: (rejectedFiles) => {
      if (rejectedFiles.length) {
        if (onReject) {
          onReject(rejectedFiles);
        } else {
          showNotification(`Could not upload files. Please contact us for assistance`, 'error');
        }
      }
    },
    onError: (err) => showNotification(err.message, 'error'),
  });

  return (
    <div {...getRootProps()} className={cx(classes.root, { [classes.rootDisabled]: buttonProps?.disabled })}>
      <input {...getInputProps(inputProps)} style={{ display: 'none' }} accept={uploadingFileInfo.acceptString} />
      {buttonProps && (
        <Button
          {...buttonProps}
          tooltip={
            withTooltip
              ? t('accepted_file_types_colon_xfiletypesx', {
                  file_types: uploadingFileInfo.acceptTooltip,
                })
              : undefined
          }
        />
      )}
      {children}
    </div>
  );
};

// ****************************************************
// * !! DO NOT USE COMPONENT DIRECTLY              !! *
// * !! USE SingleFileDropzone / MultiFileDropzone !! *
// ****************************************************

export const FileDropzone = withStyles(styles)(FileDropzoneComponent);
